// The expanded-row detail panel for an activity entry. It reads the wire
// `details`, whose shape varies per module + action, and renders whatever it
// carries:
//   • update  → `changes` (scalar field edits) as "Field  ~Old~ → New" rows;
//               `permissionChanges` / `columns` / `options` as add/remove/update
//               toggle rows; `people` / `tags` as membership deltas.
//   • create  → `permissions` as a "Capabilities" row; a snapshot of the new
//               entity's scalar attributes (type, priority, …) as value rows;
//               and a form set's option list. A project's create entry is the
//               exception: it renders the bare empty state (per design), its
//               stored snapshot kept for audit only.
//   • delete  → the entity is gone, so one row shows what it was (struck
//               through) → Deleted, labelled by the entity type.
// A meta line (entry code · category · full timestamp) sits above the rows.

import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/common';
import { ActivityLogAction, type ActivityItem } from '@/api/endpoints/activityLogApi';
import { categoryLabel, entityLabel, fmtFullDateTime, targetName } from './activityMeta';

/** A single on/off state token. `from` renders muted + struck-through (the
 *  prior value); the `to` token renders emphasised in its state's tone. */
const StateToken = ({ on, from }: { on: boolean; from?: boolean }) => (
  <span
    className={[
      'activity-detail__state',
      from ? 'activity-detail__state--from' : `activity-detail__state--${on ? 'on' : 'off'}`,
    ].join(' ')}
  >
    {on ? 'On' : 'Off'}
  </span>
);

const Arrow = () => (
  <Icon name='arrow-right' size={14} className='activity-detail__arrow' aria-hidden='true' />
);

/** Known scalar field slugs → translated label; unknown fall back to the slug.
 *  Shared by the `changes` (before→after) rows and the create snapshot rows. */
const FIELD_LABEL_KEYS: Record<string, string> = {
  name: 'activity_log_field_name',
  description: 'activity_log_field_description',
  status: 'activity_log_field_status',
  location: 'activity_log_field_location',
  googleMapLink: 'activity_log_field_map_link',
  coordinates: 'activity_log_field_coordinates',
  title: 'activity_log_field_title',
  priority: 'activity_log_field_priority',
  schemaName: 'activity_log_field_schema_name',
  dataSourceType: 'activity_log_field_data_source_type',
  externalId: 'activity_log_field_external_id',
  fieldTitle: 'activity_log_field_field_title',
  dataSourceColumn: 'activity_log_field_data_source_column',
  isHidden: 'activity_log_field_is_hidden',
  sqlColumn: 'activity_log_field_sql_column',
  type: 'activity_log_field_type',
  color: 'activity_log_field_color',
  startDate: 'activity_log_field_start_date',
  endDate: 'activity_log_field_end_date',
  dueDate: 'activity_log_field_due_date',
  state: 'activity_log_field_state',
  groupName: 'activity_log_field_group_name',
  sectionName: 'activity_log_field_section_name',
  section: 'activity_log_field_section',
  allowJoinByLink: 'activity_log_field_allow_join',
  projectType: 'activity_log_field_project_type',
  icon: 'activity_log_field_icon',
  folder: 'activity_log_field_folder',
  submitButtonColor: 'activity_log_field_submit_color',
  dataProtectionLevel: 'activity_log_field_data_protection',
  allowLogs: 'activity_log_field_allow_logs',
  allowWebpageResponse: 'activity_log_field_allow_webpage',
  version: 'activity_log_field_version',
  publishedVersion: 'activity_log_field_published_version',
  elementsCount: 'activity_log_field_elements_count',
  apiMethod: 'activity_log_field_api_method',
  apiEndpoint: 'activity_log_field_api_endpoint',
  isEnabled: 'activity_log_field_is_enabled',
  questions: 'activity_log_field_questions',
  steps: 'activity_log_field_steps',
  columns: 'activity_log_field_columns',
  options: 'activity_log_field_options',
  members: 'activity_log_field_members',
  lead: 'activity_log_field_lead',
  inspector: 'activity_log_field_inspector',
};

/** Fields whose from/to values are ISO dates — rendered as "30 Sep 2026". */
const DATE_FIELDS = new Set(['startDate', 'dueDate', 'endDate']);

/** Lifecycle-state slugs (form/workflow/inspection status changes) →
 *  translated labels. */
const STATUS_VALUE_KEYS: Record<string, string> = {
  draft: 'activity_log_value_draft',
  published: 'activity_log_value_published',
  active: 'activity_log_value_active',
  archived: 'activity_log_value_archived',
  suspended: 'activity_log_value_suspended',
};

// Scalar attributes shown as a value snapshot on a create entry (the entity's
// name/title is already the row's target, so it is excluded here).
const SNAPSHOT_KEYS = [
  'dataSourceType',
  'priority',
  'sqlColumn',
  'type',
  'color',
  'state',
  'externalId',
  'dataSourceColumn',
  'isHidden',
] as const;

const displayValue = (value: unknown): string => {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (value == null) return '—';
  return String(value);
};

const ActivityDetailPanel = ({ item }: { item: ActivityItem }) => {
  const { t, i18n } = useTranslation();
  const details = item.details;
  const changes = details?.permissionChanges ?? [];
  const selection = details?.permissions ?? [];

  const isCreate = item.action === ActivityLogAction.CREATE;
  // The design renders a project's or inspection's create entry as the bare
  // "no field-level changes" state: a brand-new entity has no before → after
  // story to tell. The stored snapshot (type, priority, initial members) is
  // kept for audit but not rendered as change rows. Other modules keep their
  // create snapshots.
  const isBareCreate = isCreate && ['projects', 'inspections'].includes(item.module);

  // Update — scalar field edits (name/description/…) as before → after rows.
  const fieldChanges = Object.entries(details?.changes ?? {});

  /** Field slug → row label, with the per-module overrides: a project's
   *  `dueDate` is called "End date" everywhere in the projects UI. */
  const fieldLabel = (field: string): string => {
    if (item.module === 'projects' && field === 'dueDate') {
      return t('activity_log_field_end_date');
    }
    return t(FIELD_LABEL_KEYS[field] ?? field);
  };

  // "30 Sep 2026" — a date-field value (stored as an ISO string).
  const fmtDateValue = (value: unknown): string | null => {
    const d = new Date(String(value));
    if (Number.isNaN(d.getTime())) return displayValue(value);
    return new Intl.DateTimeFormat(i18n.language, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(d);
  };

  /** Formats one side of a before → after row per field: question/column
   *  tallies read as "18 questions", a members tally as "6 people", date
   *  fields as "30 Sep 2026", an empty folder as "Unfiled", and
   *  lifecycle-status slugs as their translated labels. Null means "no prior
   *  value" (rendered as the muted — token). */
  const formatChangeValue = (field: string, value: unknown): string | null => {
    if (value == null || value === '') {
      return field === 'folder' ? t('activity_log_folder_unfiled') : null;
    }
    if (field === 'questions') return t('activity_log_questions_count', { value: Number(value) });
    if (field === 'columns') return t('activity_log_columns_count', { value: Number(value) });
    if (field === 'members') return t('activity_log_people_count', { value: Number(value) });
    if (DATE_FIELDS.has(field)) return fmtDateValue(value);
    if ((field === 'status' || field === 'state') && STATUS_VALUE_KEYS[String(value)]) {
      return t(STATUS_VALUE_KEYS[String(value)]);
    }
    return displayValue(value);
  };

  /** The "after" suffix naming what a tally row actually added/removed — e.g.
   *  "7 columns (+ Warranty end)", "6 people (+ Sara Nasser)" or
   *  "18 questions (+1 · −1)": counts alone can hide a one-removed-one-added
   *  edit. */
  const changeRowSuffix = (field: string): string => {
    const parts: string[] = [];
    if (field === 'columns') {
      const added = details?.columns?.added ?? [];
      const removed = details?.columns?.removed ?? [];
      if (added.length) parts.push(`+ ${added.join(', ')}`);
      if (removed.length) parts.push(`− ${removed.join(', ')}`);
    }
    if (field === 'members') {
      const added = details?.people?.added ?? [];
      const removed = details?.people?.removed ?? [];
      if (added.length) parts.push(`+ ${added.join(', ')}`);
      if (removed.length) parts.push(`− ${removed.join(', ')}`);
    }
    if (field === 'questions') {
      const added = details?.questions?.added ?? 0;
      const removed = details?.questions?.removed ?? 0;
      if (added) parts.push(`+${added}`);
      if (removed) parts.push(`−${removed}`);
    }
    return parts.length ? ` (${parts.join(' · ')})` : '';
  };

  // Membership deltas (people, tags) — one row per added/removed label. When
  // the entry also carries the members headcount transition ("4 people →
  // 6 people"), that row already names the delta in its suffix, so the
  // per-person rows are skipped (same idiom as columns/options below).
  const people = details?.changes?.members ? undefined : details?.people;
  const membershipRows = isBareCreate ? [] : [
    ...(people?.added ?? []).map((name) => ({
      key: `people:add:${name}`,
      label: `${t('activity_log_member')} · ${name}`,
      state: 'added' as const,
    })),
    ...(people?.removed ?? []).map((name) => ({
      key: `people:remove:${name}`,
      label: `${t('activity_log_member')} · ${name}`,
      state: 'removed' as const,
    })),
    ...(details?.tags?.added ?? []).map((title) => ({
      key: `tag:add:${title}`,
      label: `${t('activity_log_tag')} · ${title}`,
      state: 'added' as const,
    })),
    ...(details?.tags?.removed ?? []).map((title) => ({
      key: `tag:remove:${title}`,
      label: `${t('activity_log_tag')} · ${title}`,
      state: 'removed' as const,
    })),
    ...(details?.groups?.added ?? []).map((name) => ({
      key: `group:add:${name}`,
      label: `${t('activity_log_group')} · ${name}`,
      state: 'added' as const,
    })),
    ...(details?.groups?.removed ?? []).map((name) => ({
      key: `group:remove:${name}`,
      label: `${t('activity_log_group')} · ${name}`,
      state: 'removed' as const,
    })),
  ];

  // Column deltas (data source) and option deltas (form set) — same add /
  // update / remove shape, one row each. Column rename renders as before→after.
  // When the entry also carries a before → after tally/list row for the same
  // thing (changes.columns / changes.options), that row already tells the
  // story, so the per-item delta rows are skipped to avoid duplication.
  const columns = details?.changes?.columns ? undefined : details?.columns;
  const options = details?.changes?.options ? undefined : details?.options;
  const isOptionsDelta = options != null && !Array.isArray(options);
  const deltaRows = [
    ...(columns?.added ?? []).map((c) => ({
      key: `col:add:${c}`,
      label: `${t('activity_log_column')} · ${c}`,
      state: 'added' as const,
    })),
    ...(columns?.updated ?? []).map((c) => ({
      key: `col:upd:${c}`,
      label: `${t('activity_log_column')} · ${c}`,
      state: 'updated' as const,
    })),
    ...(columns?.removed ?? []).map((c) => ({
      key: `col:rem:${c}`,
      label: `${t('activity_log_column')} · ${c}`,
      state: 'removed' as const,
    })),
    ...(isOptionsDelta ? (options.added ?? []) : []).map((o) => ({
      key: `opt:add:${o}`,
      label: `${t('activity_log_option')} · ${o}`,
      state: 'added' as const,
    })),
    ...(isOptionsDelta ? (options.updated ?? []) : []).map((o) => ({
      key: `opt:upd:${o}`,
      label: `${t('activity_log_option')} · ${o}`,
      state: 'updated' as const,
    })),
    ...(isOptionsDelta ? (options.removed ?? []) : []).map((o) => ({
      key: `opt:rem:${o}`,
      label: `${t('activity_log_option')} · ${o}`,
      state: 'removed' as const,
    })),
  ];
  const renamed = columns?.renamed;

  // Update rows — one per changed permission, added first then removed.
  const changeRows = changes.flatMap((group) => [
    ...group.added.map((permission) => ({
      key: `${group.module}:add:${permission}`,
      label: `${group.module} · ${permission}`,
      on: true,
    })),
    ...group.removed.map((permission) => ({
      key: `${group.module}:remove:${permission}`,
      label: `${group.module} · ${permission}`,
      on: false,
    })),
  ]);

  // Create capabilities — "Module: perm, perm" segments joined by "·".
  const capabilities = selection
    .filter((group) => group.selected.length > 0)
    .map((group) => `${group.module}: ${group.selected.join(', ')}`)
    .join(' · ');

  // Create snapshot — the new entity's scalar attributes as value rows.
  const snapshotRows = isCreate && !isBareCreate
    ? SNAPSHOT_KEYS.filter((key) => details?.[key] != null).map((key) => ({
        key: `snap:${key}`,
        label: t(FIELD_LABEL_KEYS[key] ?? key),
        value: displayValue(details?.[key]),
      }))
    : [];
  // A form set snapshots its options as a plain value list on create.
  const optionList = isCreate && Array.isArray(options) ? options.join(', ') : '';

  // Delete — the entity is gone, so the row shows what it was (struck through) →
  // Deleted, labelled by the entity type ("Role").
  const isDelete = item.action === ActivityLogAction.DELETE;

  const stateLabel = (state: 'added' | 'removed' | 'updated') =>
    state === 'added'
      ? t('activity_log_state_added')
      : state === 'removed'
        ? t('activity_log_state_removed')
        : t('activity_log_state_updated');

  const hasContent =
    fieldChanges.length > 0 ||
    membershipRows.length > 0 ||
    deltaRows.length > 0 ||
    renamed != null ||
    changeRows.length > 0 ||
    capabilities.length > 0 ||
    snapshotRows.length > 0 ||
    optionList.length > 0 ||
    isDelete;

  return (
    <div className='activity-detail'>
      <div className='activity-detail__meta'>
        <span>{categoryLabel(item.module, t)}</span>
        <span className='activity-detail__meta-dot'>·</span>
        <span>{fmtFullDateTime(item.createdAt)}</span>
      </div>

      {hasContent ? (
        <div className='activity-detail__rows'>
          {fieldChanges.map(([field, change]) => {
            const from = formatChangeValue(field, change.from);
            const to = formatChangeValue(field, change.to);
            return (
            <div key={`field:${field}`} className='activity-detail__row'>
              <span className='activity-detail__label'>
                {fieldLabel(field)}
              </span>
              <span className='activity-detail__transition'>
                  {from ? (
                    <span className='activity-detail__was'>{from}</span>
                ) : (
                    <span className='activity-detail__state activity-detail__state--none'>—</span>
                )}
                <Arrow />
                  <span className='activity-detail__caps'>
                    {to ? `${to}${changeRowSuffix(field)}` : '—'}
                  </span>
              </span>
            </div>
            );
          })}

          {renamed && (
            <div key='col:renamed' className='activity-detail__row'>
              <span className='activity-detail__label'>{t('activity_log_column')}</span>
              <span className='activity-detail__transition'>
                {renamed.from ? (
                  <span className='activity-detail__was'>{renamed.from}</span>
                ) : (
                  <span className='activity-detail__state activity-detail__state--none'>—</span>
                )}
                <Arrow />
                <span className='activity-detail__caps'>{renamed.to || '—'}</span>
              </span>
            </div>
          )}

          {membershipRows.map((row) => (
            <div key={row.key} className='activity-detail__row'>
              <span className='activity-detail__label'>{row.label}</span>
              <span className='activity-detail__transition'>
                <span className='activity-detail__state activity-detail__state--none'>—</span>
                <Arrow />
                <span
                  className={`activity-detail__state activity-detail__state--${
                    row.state === 'removed' ? 'removed' : 'added'
                  }`}
                >
                  {stateLabel(row.state)}
                </span>
              </span>
            </div>
          ))}

          {deltaRows.map((row) => (
            <div key={row.key} className='activity-detail__row'>
              <span className='activity-detail__label'>{row.label}</span>
              <span className='activity-detail__transition'>
                <span className='activity-detail__state activity-detail__state--none'>—</span>
                <Arrow />
                <span
                  className={`activity-detail__state activity-detail__state--${
                    row.state === 'removed' ? 'removed' : 'added'
                  }`}
                >
                  {stateLabel(row.state)}
                </span>
              </span>
            </div>
          ))}

          {changeRows.map((row) => (
            <div key={row.key} className='activity-detail__row'>
              <span className='activity-detail__label'>{row.label}</span>
              <span className='activity-detail__transition'>
                <StateToken on={!row.on} from />
                <Arrow />
                <StateToken on={row.on} />
              </span>
            </div>
          ))}

          {snapshotRows.map((row) => (
            <div key={row.key} className='activity-detail__row'>
              <span className='activity-detail__label'>{row.label}</span>
              <span className='activity-detail__transition'>
                <span className='activity-detail__state activity-detail__state--none'>—</span>
                <Arrow />
                <span className='activity-detail__caps'>{row.value}</span>
              </span>
            </div>
          ))}

          {capabilities && (
            <div className='activity-detail__row'>
              <span className='activity-detail__label'>{t('activity_log_capabilities')}</span>
              <span className='activity-detail__transition'>
                <span className='activity-detail__state activity-detail__state--none'>—</span>
                <Arrow />
                <span className='activity-detail__caps'>{capabilities}</span>
              </span>
            </div>
          )}

          {optionList && (
            <div className='activity-detail__row'>
              <span className='activity-detail__label'>{t('activity_log_option')}</span>
              <span className='activity-detail__transition'>
                <span className='activity-detail__state activity-detail__state--none'>—</span>
                <Arrow />
                <span className='activity-detail__caps'>{optionList}</span>
              </span>
            </div>
          )}

          {isDelete && details?.rows != null ? (
            // A data source's delete row shows what was lost: its row tally
            // ("1,204 rows"), struck through → Deleted.
            <div className='activity-detail__row'>
              <span className='activity-detail__label'>{t('activity_log_field_rows')}</span>
              <span className='activity-detail__transition'>
                <span className='activity-detail__was'>
                  {t('activity_log_rows_count', {
                    value: Number(details.rows).toLocaleString(),
                  })}
                </span>
                <Arrow />
                <span className='activity-detail__state activity-detail__state--deleted'>
                  {t('activity_log_state_deleted')}
                </span>
              </span>
            </div>
          ) : (
            isDelete && (
            <div className='activity-detail__row'>
              <span className='activity-detail__label'>{entityLabel(item.module, t)}</span>
              <span className='activity-detail__transition'>
                <span className='activity-detail__was'>{targetName(item)}</span>
                <Arrow />
                <span className='activity-detail__state activity-detail__state--deleted'>
                  {t('activity_log_state_deleted')}
                </span>
              </span>
            </div>
            )
          )}
        </div>
      ) : (
        <div className='activity-detail__empty'>{t('activity_log_details_none')}</div>
      )}
    </div>
  );
};

export default ActivityDetailPanel;
